import { formatDistance, t } from "../i18n/index.ts";
import { observeRouteThumbnails, QUEST_LIST_THUMB_PLACEHOLDER } from "../quest/route-thumbnail-pool.ts";
import { paintQuestSaveHeart, questSaveHeartEnabled } from "../quest/save-heart.ts";
import { walkLabel, formatMinutes } from "../quest/summary.ts";
import type { NearbyQuest } from "../quest/types.ts";
import { isSpotOnlyListQuest, questListRouteLengthM } from "../../shared/quest-feed.ts";
import { questListMetaLine, rememberThumbSource, spotCountLabel } from "./home-list.ts";
import type { NearbyViewerAt } from "./nearby-viewer.ts";

/** Right detail panel elements the list preview (summary) paints. */
export interface ListPreviewElements {
  body: HTMLElement;
  title: HTMLElement;
  badge: HTMLElement;
  distance: HTMLElement;
  pointsLabel: HTMLElement;
  points: HTMLElement;
  strip: HTMLElement;
  meta: HTMLElement;
  status: HTMLElement;
  publish: HTMLElement;
  edit: HTMLElement;
  questEdit: HTMLAnchorElement;
  expand: HTMLElement;
  remove: HTMLElement;
  geojson: HTMLElement;
  gpx: HTMLElement;
  saveHeart: HTMLButtonElement;
  thumb: HTMLImageElement;
}

/**
 * Right panel for a list card before the full quest loads: title, distance, walk time,
 * save heart, a route thumbnail, and an expand button that fetches the full detail.
 */
export class ListPreviewPanel {
  private readonly els: ListPreviewElements;
  private readonly whenMapReady: (run: () => void) => void;

  constructor(els: ListPreviewElements, whenMapReady: (run: () => void) => void) {
    this.els = els;
    this.whenMapReady = whenMapReady;
  }

  /** Summary vs full detail: hide the preview-only thumbnail and expand button. */
  hidePreviewChrome(): void {
    this.els.thumb.hidden = true;
    this.els.expand.hidden = true;
  }

  paint(item: NearbyQuest, saveBusy: boolean, viewer: NearbyViewerAt | null = null): void {
    const e = this.els;
    e.title.textContent = item.title.trim() || t("quests.untitled");
    e.badge.hidden = true;
    const routeLen = questListRouteLengthM(item);
    const spotOnly = isSpotOnlyListQuest(item);
    if (spotOnly) {
      e.distance.textContent = spotCountLabel(item.spot_count ?? item.spots_preview?.length ?? 0);
      e.pointsLabel.textContent = t("home.estTime");
      e.points.textContent = formatMinutes(item.est_minutes_total ?? 0);
    } else {
      e.distance.textContent = formatDistance(routeLen);
      e.pointsLabel.textContent = t("home.estTime");
      e.points.textContent = walkLabel(routeLen);
    }
    e.strip.hidden = true;
    e.meta.textContent = questListMetaLine(item, viewer);
    e.status.hidden = true;
    e.publish.hidden = true;
    e.edit.hidden = true;
    e.questEdit.hidden = true;
    e.questEdit.removeAttribute("href");
    e.expand.hidden = false;
    e.remove.hidden = true;
    e.geojson.hidden = true;
    e.gpx.hidden = true;
    this.paintSaveHeart({ status: item.status, saved: item.saved }, saveBusy);
    rememberThumbSource(item);
    e.thumb.hidden = false;
    e.thumb.dataset.thumbId = item.id;
    e.thumb.removeAttribute("data-thumb-ready");
    e.thumb.classList.add("is-placeholder");
    e.thumb.classList.remove("is-empty");
    e.thumb.src = QUEST_LIST_THUMB_PLACEHOLDER;
    observeRouteThumbnails(e.body);
    this.whenMapReady(() => observeRouteThumbnails(e.body));
  }

  /** Save heart for a published quest (preview or full detail); hidden for drafts. */
  paintSaveHeart(quest: { status: string; saved?: boolean }, busy: boolean): void {
    const show = questSaveHeartEnabled(quest.status);
    this.els.saveHeart.hidden = !show;
    if (!show) return;
    paintQuestSaveHeart(this.els.saveHeart, quest.saved === true, busy);
  }
}
